import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  ButtonGroup,
  Badge,
  Spinner,
  Table,
  Row,
  Col,
  CustomInput,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { getPlanById } from 'config/subscriptionPlans';
import {
  activateGroupOrderPaidCodes,
  fetchGroupOrderDetails,
} from 'redux/GroupOrders/GroupOrdersActions';

const GroupOrderDetailsModal = ({ isOpen, toggle, orderDetails, loading, activatingPaidCodes }) => {
  const dispatch = useDispatch();
  const [filter, setFilter] = React.useState('all');

  if (loading || !orderDetails) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>Order Details</ModalHeader>
        <ModalBody className="text-center py-5">
          <Spinner />
        </ModalBody>
      </Modal>
    );
  }

  const { order, codes } = orderDetails;
  const isIndividual = order.paymentType === 'individual';

  const unclaimed = codes.filter((c) => c.status === 'unclaimed');
  const claimed = codes.filter((c) => c.status === 'claimed');
  const paidCodes = isIndividual ? codes.filter((c) => c.paymentStatus === 'paid') : [];
  const unpaidCodes = isIndividual ? codes.filter((c) => c.paymentStatus !== 'paid') : [];
  const activatedCodes = isIndividual ? codes.filter((c) => c.subscriptionActivated) : [];
  const paidNotActivated = isIndividual ? codes.filter((c) => c.paymentStatus === 'paid' && !c.subscriptionActivated) : [];

  const copyAllCodes = () => {
    const codeList = codes.map((c) => c.code).join('\n');
    navigator.clipboard.writeText(codeList).then(() => {
      Swal.fire({
        title: 'Copied!',
        text: `${codes.length} codes copied to clipboard.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    });
  };

  const copyUnclaimedCodes = () => {
    const codeList = unclaimed.map((c) => c.code).join('\n');
    navigator.clipboard.writeText(codeList).then(() => {
      Swal.fire({
        title: 'Copied!',
        text: `${unclaimed.length} unclaimed codes copied to clipboard.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    });
  };

  const handleToggleAutoActivate = () => {
    const newValue = !order.autoActivate;
    if (newValue && paidNotActivated.length > 0) {
      Swal.fire({
        title: 'Enable Auto-Activate?',
        text: `This will activate ${paidNotActivated.length} paid user(s) now and auto-activate future payments.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#2dce89',
        confirmButtonText: 'Enable',
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(activateGroupOrderPaidCodes(order.id, true)).then((data) => {
            Swal.fire('Enabled!', data.message, 'success');
            dispatch(fetchGroupOrderDetails(order.id));
          }).catch(() => {
            Swal.fire('Error', 'Failed to enable auto-activation', 'error');
          });
        }
      });
    } else if (newValue) {
      dispatch(activateGroupOrderPaidCodes(order.id, true)).then((data) => {
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Auto-activate enabled', showConfirmButton: false, timer: 1500 });
        dispatch(fetchGroupOrderDetails(order.id));
      }).catch(() => {
        Swal.fire('Error', 'Failed to enable auto-activation', 'error');
      });
    } else {
      dispatch(activateGroupOrderPaidCodes(order.id, false)).then(() => {
        Swal.fire({ toast: true, position: 'top-end', icon: 'info', title: 'Auto-activate disabled', showConfirmButton: false, timer: 1500 });
        dispatch(fetchGroupOrderDetails(order.id));
      }).catch(() => {
        Swal.fire('Error', 'Failed to disable auto-activation', 'error');
      });
    }
  };

  const getStatusBadge = (status) => {
    const map = { pending: 'warning', paid: 'success', active: 'info', expired: 'secondary' };
    return <Badge color={map[status] || 'info'}>{status}</Badge>;
  };

  const getCodePaymentBadge = (code) => {
    if (code.subscriptionActivated) return <Badge color="success">Activated</Badge>;
    if (code.paymentStatus === 'paid') return <Badge color="primary">Paid</Badge>;
    return <Badge color="warning">Unpaid</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Determine filtered codes based on filter and order type
  const getFilteredCodes = () => {
    if (isIndividual) {
      switch (filter) {
        case 'unpaid': return unpaidCodes;
        case 'paid': return paidCodes.filter((c) => !c.subscriptionActivated);
        case 'activated': return activatedCodes;
        default: return codes;
      }
    }
    switch (filter) {
      case 'available': return unclaimed;
      case 'redeemed': return claimed;
      default: return codes;
    }
  };

  const filteredCodes = getFilteredCodes();

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {order.programName} — Order Details
      </ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col md="6">
            <p className="mb-1">
              <strong>Contact:</strong> {order.contactName}
            </p>
            <p className="mb-1">
              <strong>Email:</strong> {order.contactEmail}
            </p>
            <p className="mb-1">
              <strong>Status:</strong> {getStatusBadge(order.status)}
            </p>
            {isIndividual && (
              <p className="mb-1">
                <strong>Payment Type:</strong>{' '}
                <Badge color="info">Individual</Badge>
              </p>
            )}
            {order.year && (
              <p className="mb-1">
                <strong>Year:</strong> {order.year}
              </p>
            )}
          </Col>
          <Col md="6">
            {order.items ? (
              <>
                <p className="mb-1"><strong>Plans:</strong></p>
                {order.items.map((item, idx) => (
                  <p key={idx} className="mb-0 ml-2" style={{ fontSize: '0.9rem' }}>
                    {item.quantity}x {getPlanById(item.planId)?.name || item.planId} — ${item.pricePerSeat}/seat (${item.subtotal})
                  </p>
                ))}
              </>
            ) : (
              <>
                <p className="mb-1">
                  <strong>Plan:</strong> {getPlanById(order.planId)?.name || order.planId}
                </p>
                <p className="mb-1">
                  <strong>Per Seat:</strong> ${order.pricePerSeat}
                </p>
              </>
            )}
            <p className="mb-1 mt-1">
              <strong>Seats:</strong> {order.classSize}
            </p>
            <p className="mb-1">
              <strong>Total:</strong> ${order.totalAmount} ({order.discountPercent}% off)
            </p>
            {isIndividual && (
              <p className="mb-1">
                <strong>Payments:</strong> {paidCodes.length}/{codes.length} paid
              </p>
            )}
          </Col>
        </Row>

        {/* Auto-Activate Toggle */}
        {isIndividual && (
          <div className="d-flex align-items-center px-3 py-2 mb-3 rounded" style={{ background: '#f6f9fc', border: '1px solid #e9ecef' }}>
            <CustomInput
              type="switch"
              id="autoActivateSwitch"
              checked={!!order.autoActivate}
              onChange={handleToggleAutoActivate}
              disabled={activatingPaidCodes}
              className="mr-0"
            />
            <span className="ml-2" style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
              <strong>Auto-Activate</strong>
              <span className="text-muted ml-1">
                — {order.autoActivate
                  ? 'Payments activate subscriptions immediately'
                  : 'Subscriptions activate when all members pay'}
              </span>
            </span>
            {activatingPaidCodes && <Spinner size="sm" className="ml-2" />}
          </div>
        )}

        {codes.length > 0 && (
          <>
            <hr />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                {isIndividual
                  ? `Codes — ${paidCodes.length} paid, ${activatedCodes.length} activated / ${codes.length} total`
                  : `Codes — ${claimed.length} claimed / ${codes.length} total`}
              </h5>
              <div>
                <Button size="sm" color="info" outline onClick={copyAllCodes} className="mr-1">
                  Copy All
                </Button>
                {unclaimed.length > 0 && !isIndividual && (
                  <Button size="sm" color="success" outline onClick={copyUnclaimedCodes}>
                    Copy Unclaimed ({unclaimed.length})
                  </Button>
                )}
              </div>
            </div>

            <div className="mb-3">
              <ButtonGroup size="sm">
                <Button
                  color={filter === 'all' ? 'primary' : 'secondary'}
                  outline={filter !== 'all'}
                  onClick={() => setFilter('all')}
                >
                  All ({codes.length})
                </Button>
                {isIndividual ? (
                  <>
                    <Button
                      color={filter === 'unpaid' ? 'warning' : 'secondary'}
                      outline={filter !== 'unpaid'}
                      onClick={() => setFilter('unpaid')}
                    >
                      Unpaid ({unpaidCodes.length})
                    </Button>
                    <Button
                      color={filter === 'paid' ? 'info' : 'secondary'}
                      outline={filter !== 'paid'}
                      onClick={() => setFilter('paid')}
                    >
                      Paid ({paidCodes.filter((c) => !c.subscriptionActivated).length})
                    </Button>
                    <Button
                      color={filter === 'activated' ? 'success' : 'secondary'}
                      outline={filter !== 'activated'}
                      onClick={() => setFilter('activated')}
                    >
                      Activated ({activatedCodes.length})
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      color={filter === 'available' ? 'warning' : 'secondary'}
                      outline={filter !== 'available'}
                      onClick={() => setFilter('available')}
                    >
                      Available ({unclaimed.length})
                    </Button>
                    <Button
                      color={filter === 'redeemed' ? 'success' : 'secondary'}
                      outline={filter !== 'redeemed'}
                      onClick={() => setFilter('redeemed')}
                    >
                      Redeemed ({claimed.length})
                    </Button>
                  </>
                )}
              </ButtonGroup>
            </div>

            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table size="sm" hover>
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Access</th>
                    {isIndividual && <th>Price</th>}
                    <th>{isIndividual ? 'Payment' : 'Status'}</th>
                    <th>Redeemed By</th>
                    <th>{isIndividual ? 'Paid At' : 'Claimed At'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCodes.map((code, idx) => (
                    <tr key={code.id}>
                      <td>{idx + 1}</td>
                      <td>
                        <code
                          style={{ fontSize: '0.95rem', cursor: 'pointer' }}
                          title="Click to copy"
                          onClick={() => {
                            navigator.clipboard.writeText(code.code);
                            Swal.fire({
                              toast: true,
                              position: 'top-end',
                              icon: 'success',
                              title: 'Copied!',
                              showConfirmButton: false,
                              timer: 800,
                            });
                          }}
                        >
                          {code.code} <i className="fas fa-copy text-muted" style={{ fontSize: '0.7rem' }} />
                        </code>
                      </td>
                      <td>
                        <small>{getPlanById(code.planId)?.months || '?'} {getPlanById(code.planId)?.months === 1 ? 'Month' : 'Months'}</small>
                      </td>
                      {isIndividual && (
                        <td><small>${code.priceAmount}</small></td>
                      )}
                      <td>
                        {isIndividual ? getCodePaymentBadge(code) : (
                          <Badge color={code.status === 'claimed' ? 'success' : 'warning'}>
                            {code.status === 'claimed' ? 'Redeemed' : 'Available'}
                          </Badge>
                        )}
                      </td>
                      <td>
                        {code.claimedByHandle || code.paidByHandle || (
                          <span className="text-muted">{'\u2014'}</span>
                        )}
                      </td>
                      <td>
                        {isIndividual
                          ? (code.paidAt ? formatDate(code.paidAt) : <span className="text-muted">{'\u2014'}</span>)
                          : (code.claimedAt ? formatDate(code.claimedAt) : <span className="text-muted">{'\u2014'}</span>)
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {codes.length === 0 && order.status === 'pending' && (
          <p className="text-muted text-center">
            Codes will be generated after payment is completed.
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GroupOrderDetailsModal;
